import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { pollMonitoring } from '../orchestration-polling.util';
import { TranslateModule } from '@ngx-translate/core';
import { Loading } from '@components/ui/loading/loading';
import { OperationMonitoringService as OperationMonitoringApiService } from '@6grain/masofa-monolith-api-client/common/api/operationMonitoring.service';
import { DatabaseConnectionsSnapshot } from '@6grain/masofa-monolith-api-client/common/model/databaseConnectionsSnapshot';
import { OperationMonitoringStatistics } from '@6grain/masofa-monolith-api-client/common/model/operationMonitoringStatistics';
import { OrchestrationCatalogSearchComponent } from '@app/pages/admin/components/orchestration-catalog-search/orchestration-catalog-search';
import { OrchestrationDatabaseSlotBoardComponent } from '@app/pages/admin/components/orchestration-database-slot-board/orchestration-database-slot-board';
import { OrchestrationObjectStorageBoardComponent } from '@app/pages/admin/components/orchestration-object-storage-board/orchestration-object-storage-board';
import { ObjectStorageSnapshot } from '@6grain/masofa-monolith-api-client/common/model/objectStorageSnapshot';
import { filterByCatalogQuery } from '@app/pages/admin/orchestration-catalog-search.util';
import { processGroupSearchHaystack } from '@app/pages/admin/orchestration-process-groups.util';
import { moduleCardsFromStatistics } from '@app/pages/admin/orchestration-overview.util';

@Component({
  selector: 'app-orchestration-overview-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    Loading,
    OrchestrationCatalogSearchComponent,
    OrchestrationDatabaseSlotBoardComponent,
    OrchestrationObjectStorageBoardComponent,
  ],
  templateUrl: './orchestration-overview-page.html',
  styleUrl: './orchestration-overview-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrchestrationOverviewPageComponent implements OnInit, OnDestroy {
  private readonly monitoring = inject(OperationMonitoringApiService);
  private readonly destroy$ = new Subject<void>();

  readonly loading = signal(true);
  readonly errorMessage = signal('');
  readonly statistics = signal<OperationMonitoringStatistics | null>(null);
  readonly searchQuery = signal('');
  readonly connectionsLoading = signal(true);
  readonly connectionsError = signal('');
  readonly connections = signal<DatabaseConnectionsSnapshot | null>(null);
  readonly storageLoading = signal(true);
  readonly storageError = signal('');
  readonly storageSnapshot = signal<ObjectStorageSnapshot | null>(null);

  readonly moduleCards = computed(() => moduleCardsFromStatistics(this.statistics()));
  readonly visibleCards = computed(() => filterByCatalogQuery(
    this.moduleCards(),
    this.searchQuery(),
    module => processGroupSearchHaystack(module),
  ));
  readonly workersOnline = computed(() => this.statistics()?.workers?.online ?? 0);
  readonly workersTotal = computed(() => this.statistics()?.workers?.total ?? 0);

  ngOnInit(): void {
    this.loadStatistics();
    this.loadConnections();
    this.loadObjectStorage();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadStatistics(): void {
    pollMonitoring(() => this.monitoring.monitoringStatisticsGet())
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        if (response) {
          this.statistics.set(response);
          this.errorMessage.set('');
        } else if (!this.statistics()) {
          this.errorMessage.set('Failed to load orchestration overview');
        }
        this.loading.set(false);
      });
  }

  private loadConnections(): void {
    pollMonitoring(() => this.monitoring.monitoringDatabaseConnectionsGet())
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        if (response) {
          this.connections.set(response);
          this.connectionsError.set('');
        } else if (!this.connections()) {
          this.connectionsError.set('Failed to load database connections');
        }
        this.connectionsLoading.set(false);
      });
  }

  private loadObjectStorage(): void {
    pollMonitoring(() => this.monitoring.monitoringObjectStorageGet())
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        if (response) {
          this.storageSnapshot.set(response);
          this.storageError.set('');
        } else if (!this.storageSnapshot()) {
          this.storageError.set('Failed to load object storage');
        }
        this.storageLoading.set(false);
      });
  }
}
